import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Text,
  Input,
  HStack,
  Button,
  FormLabel,
} from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryClient } from '$/services/fetcher';
import {
  adminLanguagesQuery,
  updateLanguage,
} from '$/services/usecases/admin/languages';

interface Props {
  editLanguageId: string | null;
  onClose: () => void;
  initialCode?: string;
}

export const EditLanguageModal: FC<Props> = ({
  editLanguageId,
  onClose,
  initialCode,
}) => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const { mutate, isPending } = useMutation({
    mutationFn: updateLanguage,
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: adminLanguagesQuery.queryKey,
      });
      onCloseModal();
    },
  });

  useEffect(() => {
    setCode(initialCode ?? '');
  }, [initialCode]);

  const onCloseModal = () => {
    onClose();
    setCode('');
  };

  if (!editLanguageId) {
    return null;
  }

  return (
    <Modal isCentered isOpen={editLanguageId != null} onClose={onCloseModal}>
      <ModalOverlay />
      <ModalContent gap='6' minW='500px' p='10'>
        <ModalHeader p='0'>
          <ModalCloseButton top='10' right='10' />

          <Text pt='1' fontSize='h7'>
            {t('admin.languages.editLanguage')}
          </Text>
        </ModalHeader>

        <ModalBody
          as='form'
          p='0'
          onSubmit={(e) => {
            e.preventDefault();
            mutate({ id: editLanguageId, legalPageCode: code });
          }}
        >
          <Text fontWeight='bold'>{t(`languages.${editLanguageId}`)}</Text>
          <FormLabel fontSize='sm'>
            {t('admin.languages.legalOverwritePlaceholder')}
          </FormLabel>
          <Input
            onChange={(event) => setCode(event.target.value)}
            placeholder={t('admin.languages.legalOverwrite')}
            value={code}
          />
          <HStack justifyContent='space-between' w='full' mt='4'>
            <Button
              color='lighterText'
              fontSize='sm'
              onClick={onCloseModal}
              variant='ghost'
            >
              {t('general.cancel')}
            </Button>
            <Button
              px='6'
              py='3'
              fontSize='sm'
              isLoading={isPending}
              type='submit'
              variant='primary'
            >
              {t('admin.languages.editLanguage')}
            </Button>
          </HStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
